<template>
    <BaseModal name="loader" :full="true" :transparent="!!data.light">
        <template v-slot:content>
            <div class="loader-box"
                :class="{ light: data.light }"
                :style="{ 'background-image': `url(${baseUrl}/themes/${theme}/init/${id}.jpg)` }"
            >
                <div class="description">
                    <label>{{ title }}</label>
                    <span>{{ slogan }}</span>
                </div>

                <div class="bg"></div>
                <Loader class="opacity-mode" />
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { onMounted, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseModal from '🔗/components/modals/BaseModal.vue';
import Loader from '🔗/components/ui/Loader.vue';

import { storeToRefs } from 'pinia';
import stores from '@/stores';
import { baseUrl } from '🔗/utils/utils';

const others = stores.others();
const modals = stores.modals();
const { theme } = storeToRefs(others);

const data = reactive({
    light: false
});

const id = theme.value === 'light' ? Math.floor(Math.random() * 6) + 1 : Math.floor(Math.random() * 10) + 1; 

const { tm, rt } = useI18n();

const title = computed(() => {
    const titles = Array.isArray(tm('modals.loader.titles')) ? tm('modals.loader.titles').map(item => rt(item)) : [];

    return titles[Math.floor(Math.random() * titles.length)];
});

const slogan = computed(() => {
    const slogans = Array.isArray(tm('modals.loader.slogans')) ? tm('modals.loader.slogans').map(item => rt(item)) : [];

    return slogans[Math.floor(Math.random() * slogans.length)];
});


onMounted(() => {
    const payload = modals.getData('loader');

    data.light = !!payload?.light;
});
</script>

<style lang="scss" scoped>
    .loader-box {
        position: fixed;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background-size: cover;
        background-position: left top;

        &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            right: 0;
            background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 40%, var(--white-500) 95%, var(--white-500) 100%);
        }

        .description {
            display: flex;
            position: absolute;
            bottom: 32px;
            left: 0;
            right: 0;
            flex-direction: column;
            gap: 12px;
            opacity: 0;
            align-items: center;
            animation: loader-description-fade-up 1s forwards;

            @keyframes loader-description-fade-up {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }
            label {
                font: var(--font-h5);
                font-weight: bold;
                color: var(--dark-500);
                text-transform: uppercase;
                text-align: center;
            }
            span {
                font: var(--font-body-2);
                color: var(--dark-500);
                text-align: center;
            }
        }

        .bg {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            right: 0;
            background: var(--white-500);
            -webkit-mask-size: 100%;
            -webkit-mask-repeat: no-repeat;
            -webkit-mask-position: left top, left bottom;
            -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
            animation: bg-gradient 1.4s;

            &:after {
                position: absolute;

                background-image: linear-gradient(222deg, var(--light-20) 17px, transparent 18px), linear-gradient(45deg, var(--light-10) 17px, transparent 18px);
                background-size: 52px 52px;
                background-repeat: repeat;

                content: "";
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                animation: bilbo-loader infinite 20s;
            }

            @keyframes bilbo-loader {
                0% {
                    background-position-x: 0%;
                    background-position-y: 0%;
                }
                100% { 
                    background-position-x: 100%;
                    background-position-y: 100%;
                }
            }

            @keyframes bg-gradient {
                0% {
                    -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 100%);
                }
                74% {
                    -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 100%);
                }
                @for $i from 0 through 25 {
                    #{$i * 1% + 75%} {
                        -webkit-mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) $i * 3%, rgba(0, 0, 0, 1) $i * 3% + 20%, rgba(0, 0, 0, 1) 100%);
                    }
                }
            }
        }


        &.light {
            background: var(--white-20) !important;
            &:before {
                display: none;
            }
            .bg,
            .description {
                display: none;
            }
            .opacity-mode {
                height: 75%;
            }
        }
    }
</style>
